import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Pagination, PaginationDefaults } from 'src/models/general/pagination.model';
import { StorageKey } from 'src/models/general/storage.key.enum';


interface PaginationService {
	storageKey: StorageKey;

	updatePagination(pagination: Pagination): void;

	getPaginationObservable(): Observable<Pagination>;

	goToFirstPage(): void;
}

@Injectable({
	providedIn: 'root'
})
export abstract class BasePaginationService implements PaginationService {
	private paginationSubject!: BehaviorSubject<Pagination>;
	private pagination!: Pagination;
	storageKey!: StorageKey;

	getPaginationObservable(): Observable<Pagination> {
		if (!this.storageKey || !this.paginationSubject) {
			throw Error(`Not initialized - no storageKey or paginationSubject found. 'initPaginationValues' needs to be called beforehand`);
		}
		return this.paginationSubject.asObservable();
	}

	updatePagination(pagination: Pagination): void {
		if (!this.storageKey) {
			throw Error(`No storageKey set - subclasses need to call 'init' with the storageKey`);
		}
		this.pagination = pagination;
		this.savePaginationState();
		this.publishPaginationChanges();
	}

	goToFirstPage(): void {
		this.goToPage(0); // Zero-indexed
	}

	private goToPage(pageNumber: number): void {
		let newPage = pageNumber;
		if (isNaN(pageNumber) || pageNumber < 0) {
			newPage = 0; // Zero-Indexed
		}

		this.pagination.currentPage = newPage;
		this.publishPaginationChanges();
	}

	protected init(storageKey: StorageKey): void {
		if (!storageKey) {
			throw Error('StorageKey missing');
		}

		this.storageKey = storageKey;
		let initialValues = this.getPaginationFromStorage();
		if (!initialValues) {
			initialValues = new PaginationDefaults();
		}
		this.pagination = initialValues;
		this.paginationSubject = new BehaviorSubject<Pagination>(initialValues);
	}

	private savePaginationState(): void {
		localStorage.setItem(this.storageKey, JSON.stringify(this.pagination));
	}

	private getPaginationFromStorage(): Pagination | null {
		const storedPagination = localStorage.getItem(this.storageKey);

		if (!storedPagination) {
			return null;
		}
		return JSON.parse(storedPagination);
	}

	private publishPaginationChanges(): void {
		this.paginationSubject.next(this.pagination);
	}

}


